// src/ui/ConfirmModal.ts
//
// Phase 12 (BALL-10): reusable Yes/No confirmation modal. Used by ShopScene's
// "replace ball" flow; planned reuse in Phase 15 for cell-mutator targeting.
//
// Layout follows the v1 modal depth-stacking convention:
//   - depth 200: full-screen dimmed overlay (catches stray taps; clickjacking guard)
//   - depth 201: centered panel with stroked border
//   - depth 202: title text, button rectangles
//   - depth 203: button labels
//
// Per PITFALLS.md UX guidance: explicit Yes/No buttons; no tap-anywhere-to-confirm.

import Phaser from 'phaser';
import { addCrispText } from './textHelper';

export interface ConfirmModalOpts {
  title: string;
  confirmLabel?: string; // defaults to 'Yes'
  cancelLabel?: string;  // defaults to 'No'
  onConfirm: () => void;
  onCancel?: () => void;
}

export class ConfirmModal {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container | null = null;
  // 12-REVIEW ME-01: in-flight guard. Phaser's scene.restart() (commonly
  // called from onConfirm) is async — there is a 1-2 frame window between
  // close() destroying the container and the restart tick actually firing.
  // A double-tap on Yes during that window can re-fire onConfirm (or hit
  // an underlying ShopScene item). Setting `resolved = true` in the Yes/No
  // handlers and gating subsequent dispatches with it makes the resolution
  // strictly idempotent.
  private resolved = false;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  open(opts: ConfirmModalOpts): void {
    if (this.container) return; // singleton-per-scene; ignore double-open
    this.resolved = false;

    // Depth 200: full-screen dimmed overlay (also blocks underlying taps).
    const overlay = this.scene.add
      .rectangle(0, 0, 375, 667, 0x000000, 0.85)
      .setOrigin(0, 0)
      .setDepth(200)
      .setInteractive(); // captures taps so the underlying scene doesn't receive them
    // 12-REVIEW ME-04: explicit no-op pointerdown handler. setInteractive()
    // alone relies on Phaser's hit-test to absorb taps via topmost-z-order
    // selection, which is fragile if any underlying scene object is at
    // depth ≥ 200 or if depth ordering hasn't settled mid-scene-restart.
    // Attaching a handler makes the absorb explicit (clickjacking guard).
    overlay.on('pointerdown', () => {
      // intentional no-op — taps outside Yes/No are absorbed here
    });

    // Depth 201: centered panel with gold border (matches v1 modal style).
    const panel = this.scene.add
      .rectangle(187, 320, 320, 200, 0x0a3d20)
      .setOrigin(0.5)
      .setStrokeStyle(2, 0xffd700)
      .setDepth(201);

    // Depth 202: title text.
    const title = addCrispText(this.scene, 187, 280, opts.title, {
      color: '#ffffff',
      fontSize: '14px',
      align: 'center',
      wordWrap: { width: 280 },
    })
      .setOrigin(0.5)
      .setDepth(202);

    // Depth 202: Yes button (left).
    const yesBtn = this.scene.add
      .rectangle(120, 360, 120, 36, 0x064020)
      .setStrokeStyle(1, 0xffffff)
      .setDepth(202)
      .setInteractive({ useHandCursor: true });

    // Depth 203: Yes label.
    const yesLbl = addCrispText(this.scene, 120, 360, opts.confirmLabel ?? 'Yes', {
      color: '#ffd700',
      fontSize: '13px',
    })
      .setOrigin(0.5)
      .setDepth(203);

    // Depth 202: No button (right).
    const noBtn = this.scene.add
      .rectangle(254, 360, 120, 36, 0x333333)
      .setStrokeStyle(1, 0xffffff)
      .setDepth(202)
      .setInteractive({ useHandCursor: true });

    // Depth 203: No label.
    const noLbl = addCrispText(this.scene, 254, 360, opts.cancelLabel ?? 'No', {
      color: '#ffffff',
      fontSize: '13px',
    })
      .setOrigin(0.5)
      .setDepth(203);

    this.container = this.scene.add
      .container(0, 0, [overlay, panel, title, yesBtn, yesLbl, noBtn, noLbl])
      .setDepth(200);

    yesBtn.on('pointerdown', () => {
      // 12-REVIEW ME-01: idempotency guard against double-tap during the
      // close()→scene.restart() race window.
      if (this.resolved) return;
      this.resolved = true;
      // Disable button hit-testing immediately — defense-in-depth so even
      // if a queued pointer event slips past the `resolved` flag check, it
      // can't reach the handler again.
      yesBtn.disableInteractive();
      noBtn.disableInteractive();
      this.close();
      opts.onConfirm();
    });
    noBtn.on('pointerdown', () => {
      if (this.resolved) return;
      this.resolved = true;
      yesBtn.disableInteractive();
      noBtn.disableInteractive();
      this.close();
      opts.onCancel?.();
    });
  }

  close(): void {
    if (this.container) {
      this.container.destroy();
      this.container = null;
    }
  }

  isOpen(): boolean {
    return this.container !== null;
  }
}
